import {
  Droplets,
  CircleDot,
  Cog,
  Car,
  ClipboardCheck,
  Waves,
  Disc3,
  BatteryCharging,
  RotateCw,
  Disc2,
  Cpu,
  Settings2,
  Stethoscope,
  Wrench,
} from "lucide-react";

const entretien = [
  {
    icon: Droplets,
    title: "Révision et vidange",
    desc: "Forfaits complets (pièces + main d'œuvre) avec les meilleures huiles du marché pour optimiser les performances de votre voiture.",
    span: "md:col-span-2",
  },
  {
    icon: CircleDot,
    title: "Freinage",
    desc: "Diagnostic et remplacement des plaquettes, disques et liquides de freinage.",
  },
  {
    icon: Cog,
    title: "Distribution",
    desc: "Détection de l'usure de la courroie pour une meilleure longévité moteur.",
  },
  {
    icon: Car,
    title: "Roulements & suspensions",
    desc: "Triangles, rotules, amortisseurs, roulements — sécurité et durée de vie préservées.",
  },
  {
    icon: ClipboardCheck,
    title: "Pré-contrôle technique",
    desc: "Anticipez les réparations nécessaires avant votre contrôle technique.",
    span: "md:col-span-2",
  },
  {
    icon: Waves,
    title: "Échappement",
    desc: "Montage et réparation : moins de bruit, meilleur rendement, moins de pollution.",
  },
  {
    icon: Disc3,
    title: "Pneumatique",
    desc: "Montage, équilibrage, réparation et permutation pour un confort de conduite optimal.",
  },
  {
    icon: BatteryCharging,
    title: "Batterie",
    desc: "Diagnostic et remplacement. On vous conseille le meilleur moment pour changer.",
  },
  {
    icon: RotateCw,
    title: "Vidange boîte automatique",
    desc: "Vidange BVA avec filtre, huile ATF adaptée constructeur. Allonge la durée de vie de votre boîte.",
    span: "md:col-span-2",
  },
];

const technique = [
  {
    icon: Cpu,
    title: "Réparation moteur",
    desc: "Interventions techniques sur moteur : joint de culasse, distribution complexe, pompe à eau, injecteurs, turbo. Diagnostic et remise en état.",
    span: "md:col-span-2",
  },
  {
    icon: Settings2,
    title: "Boîte de vitesses",
    desc: "Réparation, reconditionnement ou remplacement de boîtes manuelles et automatiques.",
  },
  {
    icon: Disc2,
    title: "Changement d'embrayage",
    desc: "Remplacement du kit d'embrayage complet (disque, mécanisme, butée) avec purge hydraulique.",
  },
  {
    icon: Stethoscope,
    title: "Recherche de panne",
    desc: "Diagnostic électronique multimarque, analyse des codes défauts, investigation mécanique approfondie.",
  },
  {
    icon: Wrench,
    title: "Mécanique technique",
    desc: "Toutes interventions mécaniques complexes : grosses réparations, remise en état, expertise technique.",
    span: "md:col-span-2",
  },
];

function ServiceCard({ s, i, prefix }) {
  return (
    <article
      data-testid={`service-card-${prefix}-${i}`}
      className={`${s.span || "md:col-span-1"} group relative overflow-hidden bg-slate-50 border border-slate-200 rounded-lg p-4 md:p-7 lift hover:bg-white hover:border-blue-300 hover:shadow-xl`}
    >
      {/* Mobile: compact stacked layout */}
      <div className="flex md:hidden flex-col items-start gap-2.5">
        <div className="h-10 w-10 shrink-0 grid place-items-center rounded-md bg-white border border-slate-200 text-slate-700 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors">
          <s.icon size={18} strokeWidth={2} />
        </div>
        <h3 className="font-display font-bold text-[15px] leading-tight text-slate-900">
          {s.title}
        </h3>
      </div>

      {/* Desktop: full card with description */}
      <div className="hidden md:flex items-start gap-4">
        <div className="h-11 w-11 shrink-0 grid place-items-center rounded-md bg-white border border-slate-200 text-slate-700 group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-colors">
          <s.icon size={20} strokeWidth={2} />
        </div>
        <div>
          <h3 className="font-display font-bold text-lg text-slate-900 mb-2">
            {s.title}
          </h3>
          <p className="text-slate-600 text-[14.5px] leading-relaxed">
            {s.desc}
          </p>
        </div>
      </div>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Nos services
            </div>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-900">
              Tout ce qu'il faut, <br className="hidden md:block" />
              <span className="text-slate-500">sous un même toit.</span>
            </h2>
          </div>
          <p className="text-slate-600 max-w-sm text-[15px] leading-relaxed">
            Du simple entretien à la remise en état, notre atelier multimarque
            prend en charge votre véhicule avec sérieux et transparence.
          </p>
        </div>

        {/* Entretien courant */}
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
            Entretien courant
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {entretien.map((s, i) => (
            <ServiceCard key={s.title} s={s} i={i} prefix="entretien" />
          ))}
        </div>

        {/* Mécanique technique */}
        <div className="mt-14 mb-6 flex items-center gap-3">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">
            Mécanique technique
          </div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
            Expertise
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {technique.map((s, i) => (
            <ServiceCard key={s.title} s={s} i={i} prefix="technique" />
          ))}
        </div>
      </div>
    </section>
  );
}
